import React, {useState, useEffect, useContext} from 'react';
import {Card, CardHeader, CardBody, Input, Table, Button} from 'reactstrap';
import {toast} from 'react-toastify';
import Loader from 'components/Loader';
import PaginationWrapper from 'components/PaginationWrapper';
import {getNudgeEmails, getStatisticsCsv} from 'utils/api';
import NudgeEmailListItem from './NudgeEmailListItem';
import sortAlphabetically from 'helpers/sortAlphabetically';
import {sortByValue} from 'helpers/sortByValue';
import SortableTableHeader from 'components/SortableTableHeader';
import {ErrorContext} from 'contexts/ErrorContext';

const NudgeEmails = () => {
    const {handleErrors} = useContext(ErrorContext);

    const [searchValue, setSearchValue] = useState('');
    const [fullEmailList, setFullEmailList] = useState(null);
    const [visibleList, setVisibleList] = useState(null);
    const [activeSortProperty, setActiveSortProperty] = useState('name');
    const [activeSortDirection, setActiveSortDirection] = useState('asc');
    const [fetchingData, setFetchingData] = useState(true);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [activeMobileSortSelection, setActiveMobileSortSelection] = useState('name-asc');
    const [downloadingCsv, setDownloadingCsv] = useState(false);

    useEffect(() => {
        fetchNudgeEmailList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(fullEmailList && visibleList) {
            applyFilters(true, fullEmailList);
        }
    }, [activeSortProperty, activeSortDirection, searchValue]);

    const applyFilters = (resetPage, list) => {
        let newArr = sortByValue(list, activeSortProperty, activeSortDirection);
        if(searchValue && searchValue.length > 0) {
            let arrFilteredBySearchValue = newArr.filter(item => {
                return item.name.toLowerCase().includes(searchValue.toLowerCase());
            });
            setVisibleList(arrFilteredBySearchValue);
            resetPage && setCurrentPageNumber(1);
        } else {
            setVisibleList(newArr);
            resetPage && setCurrentPageNumber(1);
        }
    };

    const fetchNudgeEmailList = async(refresh) => {
        try {
            !fetchingData && !refresh && setFetchingData(true);
            let emailData = await getNudgeEmails();
            emailData.content.result.forEach(item => {
                if(item.hasOwnProperty('entity')) item.name = item.entity.title;
            });
            let initialList = sortAlphabetically(emailData.content.result, 'name');
            setFullEmailList(initialList);
            if(!refresh) {
                setVisibleList(initialList);
            }
            refresh && applyFilters(!refresh, initialList);
            setFetchingData(false);
        } catch(err) {
            console.log(err);
            handleErrors();
            setFetchingData(false);
        }
    };

    const handlePaginationClick = (pageNumber) => {
        setCurrentPageNumber(pageNumber.selected + 1);
    };

    const handleSortSelect = (sortValue) => {
        if(activeSortProperty === sortValue) {
            setActiveSortDirection(activeSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setActiveSortProperty(sortValue);
            setActiveSortDirection('asc');
        }
    };

    const handleMobileSortSelect = (sortSelection) => {
        let splitArr = sortSelection.split('-');
        if(splitArr.length === 2) {
            setActiveSortProperty(splitArr[0]);
            setActiveSortDirection(splitArr[1]);
        }
        setActiveMobileSortSelection(sortSelection);
    };


    const handleCsvDownload = async() => {
        try {
            setDownloadingCsv(true);
            await getStatisticsCsv();
            setDownloadingCsv(false);
        } catch(err) {
            console.log(err);
            setDownloadingCsv(false);
            toast.error('Unable to complete your request at this time');
        }
    };

    return (
        <div className="wrapper d-flex align-items-center mt-3 flex-column">
            <Card className="w-100">
                <CardHeader>
                    <h1 className="mb-0" style={{textAlign: 'center'}}>Nudge Emails</h1>
                </CardHeader>
                {visibleList === null ? (
                    <div style={{padding: '3rem 2rem'}}>
                        <Loader small text="Fetching email data"/>
                    </div>
                ) : (
                    <CardBody>
                        <div className="d-flex align-items-center justify-content-between">
                            {fullEmailList !== null && fullEmailList.length > 0 && (
                                <div className="feeds-container-wrapper">
                                    <Input
                                        style={{height: '35px'}}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        value={searchValue}
                                        placeholder="Search by entity name"
                                        type="text"
                                        className="searchInput"
                                    />
                                    {!searchValue ? (
                                        <i
                                            style={{position: 'absolute', top: '28%', bottom: '10.5px', right: '11px'}}
                                            className="fa fa-search search-container_icon"
                                        />
                                    ) : (
                                        <i
                                            style={{
                                                cursor: 'pointer',
                                                position: 'absolute',
                                                top: '28%',
                                                bottom: '10.5px',
                                                right: '11px',
                                            }}
                                            onClick={() => setSearchValue('')}
                                            className="fa fa-times-circle search-container_icon"
                                        />
                                    )}
                                </div>
                            )}
                            <Button className="calltoaction-button" onClick={handleCsvDownload} style={{width: '214.75px'}}>
                                {downloadingCsv ?
                                    <i className="fa fa-spinner fa-spin" />
                                    :
                                    <><i className="fas fa-download"/> Download Statistics CSV</>
                                }
                            </Button>
                        </div>

                        {fullEmailList && fullEmailList.length > 0 && (
                            <div className="feeds_sortBy">
                                <span>Sort by</span>
                                <Input onChange={(e) => handleMobileSortSelect(e.target.value)} type="select"
                                       value={activeMobileSortSelection}>
                                    <option value="name-asc">Entity Name (A to Z)</option>
                                    <option value="name-desc">Entity Name (Z to A)</option>
                                </Input>
                            </div>
                        )}
                        <div className="table mt-3">
                            {fullEmailList && fullEmailList.length > 0 ? (
                                <Table style={{color: '#314784'}} responsive>
                                    <thead className="hidden">
                                    <tr>
                                        <SortableTableHeader
                                            name="Entity Name"
                                            value="name"
                                            activeSortValue={activeSortProperty}
                                            activeSortDirection={activeSortDirection}
                                            handleSelect={() => handleSortSelect('name')}
                                        />
                                        <th>Preview Email</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {fetchingData ? (
                                        <tr>
                                            <td className="mt-5"><Loader/></td>
                                        </tr>
                                    ) : (
                                        <>
                                            {visibleList && visibleList.length > 0 ? (
                                                visibleList.slice((currentPageNumber - 1) * 50, currentPageNumber * 50).map((item, index) => (
                                                    <NudgeEmailListItem
                                                        key={index}
                                                        item={item}
                                                        refreshList={fetchNudgeEmailList}
                                                    />
                                                ))
                                            ) : (
                                                <tr>
                                                    <td className="mt-5">No matching results</td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="mt-5">No nudge emails found</p>
                            )}
                        </div>
                        {visibleList && visibleList.length > 50 && (
                            <PaginationWrapper
                                totalPages={Math.ceil(visibleList.length / 50)}
                                handlePageClick={handlePaginationClick}
                            />
                        )}
                    </CardBody>
                )}
            </Card>
        </div>
    );
};

export default NudgeEmails;
